import { Circle, X } from 'lucide-react'
import { cn } from '@/lib/utils'

interface QuizResultInfo {
  isCorrect: boolean | null
}

interface QuizNavChipsProps {
  total: number
  currentIndex: number
  answeredIndices: Set<number>
  solveMode: 'lesson' | 'test'
  /** lesson 모드에서 정답/오답 색 표시용. quizzes와 index로 매핑. */
  quizIds?: string[]
  resultsByQuizId?: Map<string, QuizResultInfo>
  /** lesson 모드: 풀어본 문제까지만 점프 허용. */
  lockBeyondAnswered?: boolean
  className?: string
  onSelect: (index: number) => void
}

export function QuizNavChips({
  total,
  currentIndex,
  answeredIndices,
  solveMode,
  quizIds,
  resultsByQuizId,
  lockBeyondAnswered,
  className,
  onSelect,
}: QuizNavChipsProps) {
  const maxAnswered = answeredIndices.size > 0
    ? Math.max(...Array.from(answeredIndices))
    : -1

  return (
    <div className={cn('flex max-w-full justify-end overflow-x-auto', className)}>
      <div
        className="inline-flex shrink-0 items-center gap-2"
        role="tablist"
        aria-label="문제 이동"
      >
        {Array.from({ length: total }, (_, i) => {
        const isAnswered = answeredIndices.has(i)
        const isCurrent = i === currentIndex
        const result = isAnswered && solveMode === 'lesson' && quizIds && resultsByQuizId
          ? resultsByQuizId.get(quizIds[i] ?? '')
          : undefined
        const resultStatus = !result
          ? null
          : result.isCorrect === null
            ? 'pending'
            : result.isCorrect
              ? 'correct'
              : 'incorrect'

        // lesson 모드 lock 판정: 풀어본 최대 인덱스 초과이면서 현재가 아닐 때
        const isLocked =
          !!lockBeyondAnswered && !isCurrent && !isAnswered && i > maxAnswered

        const base =
          'inline-flex size-8 shrink-0 items-center justify-center rounded-xl text-[16px] font-bold leading-none tracking-[-0.6px] transition-colors'

        let state: string
        let status: 'current' | 'correct' | 'incorrect' | 'pending' | 'locked' | 'answered' | 'idle'

        if (isCurrent && (resultStatus === 'correct' || resultStatus === 'incorrect')) {
          state = resultStatus === 'correct'
            ? 'border-2 border-lc-success-500 bg-white text-lc-success-500'
            : 'border-2 border-lc-error-500 bg-white text-lc-error-500'
          status = resultStatus
        } else if (isCurrent) {
          state = 'border-2 border-lc-key-500 bg-white text-lc-key-500'
          status = 'current'
        } else if (isLocked) {
          state = 'cursor-not-allowed bg-lc-v3-gray-300 text-lc-text-tertiary'
          status = 'locked'
        } else if (resultStatus) {
          state = resultStatus === 'correct'
            ? 'bg-lc-success-500 text-white hover:bg-lc-success-600'
            : resultStatus === 'incorrect'
              ? 'bg-lc-error-500 text-white hover:bg-lc-error-600'
              : 'bg-lc-key-500 text-white hover:bg-lc-key-600'
          status = resultStatus
        } else if (isAnswered) {
          state = solveMode === 'lesson'
            ? 'bg-lc-key-500 text-white hover:bg-lc-key-600'
            : 'bg-lc-success-500 text-white hover:bg-lc-success-600'
          status = solveMode === 'lesson' ? 'pending' : 'answered'
        } else {
          state = 'bg-lc-v3-gray-300 text-lc-text-tertiary hover:bg-lc-v3-gray-400'
          status = 'idle'
        }

        return (
          <button
            key={i}
            type="button"
            role="tab"
            aria-label={String(i + 1)}
            aria-selected={isCurrent}
            aria-disabled={isLocked}
            disabled={isLocked}
            onClick={() => !isLocked && onSelect(i)}
            data-status={status}
            className={`${base} ${state}`}
          >
            {isCurrent || !resultStatus || resultStatus === 'pending' ? i + 1 : null}
            {!isCurrent && resultStatus === 'correct' ? (
              <Circle className="size-3 fill-transparent" strokeWidth={2.5} aria-hidden />
            ) : null}
            {!isCurrent && resultStatus === 'incorrect' ? (
              <X className="size-3.5" strokeWidth={3} aria-hidden />
            ) : null}
          </button>
        )
        })}
      </div>
    </div>
  )
}
